import React, { useState } from 'react';
import { X, QrCode, MessageCircle } from 'lucide-react';
import { useTranslation } from '../contexts/TranslationContext';

const WhatsAppQRWidget = () => {
  const { t, lang } = useTranslation();
  const [showPopup, setShowPopup] = useState(false);
  
  // Your WhatsApp Business official link
  const whatsappLink = 'https://wa.me/message/2U65LOLAR66RH1';
  
  // Generate QR Code using api.qrserver.com (reliable free service)
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(whatsappLink)}&margin=10`;

  const handleStartChat = () => {
    window.open(whatsappLink, '_blank');
  };

  return (
    <>
      {/* Floating WhatsApp Button */}
      {!showPopup && (
        <button
          onClick={() => setShowPopup(true)}
          className="fixed bottom-[68px] right-5 z-50 bg-[#25D366] hover:bg-[#20BA5A] text-white rounded-full p-2.5 shadow-lg transition-all duration-300 hover:scale-110 group"
          title={lang === 'es' ? 'Chatea con nosotros en WhatsApp' : 'Chat with us on WhatsApp'}
        >
          <svg width="22" height="22" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16 0C7.164 0 0 7.164 0 16c0 2.832.744 5.488 2.048 7.792L0 32l8.416-2.208A15.91 15.91 0 0016 32c8.836 0 16-7.164 16-16S24.836 0 16 0z" fill="currentColor"/>
            <path d="M25.328 22.768c-.352.992-2.072 1.824-3.392 2.064-1.32.24-2.144.184-6.216-1.336-4.072-1.52-6.648-5.664-6.848-5.928-.2-.264-1.64-2.184-1.64-4.168 0-1.984 1.04-2.96 1.408-3.368.368-.408.8-.512 1.072-.512.272 0 .544.008.776.016.248.008.592-.096.928.704.336.8 1.152 2.816 1.256 3.016.104.2.176.44.04.704-.136.264-.2.432-.4.664-.2.232-.416.52-.592.696-.2.2-.408.416-.176.816.232.4 1.032 1.704 2.216 2.76 1.528 1.36 2.808 1.784 3.208 1.984.4.2.632.168.864-.096.232-.264.992-1.168 1.256-1.568.264-.4.528-.336.888-.2.36.136 2.296 1.08 2.688 1.28.392.2.656.296.752.456.096.16.096.928-.256 1.92z" fill="#fff"/>
          </svg>
        </button>
      )}

      {/* Pop-up Modal - Compact Version positioned above button */}
      {showPopup && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40 bg-black bg-opacity-30" 
            onClick={() => setShowPopup(false)}
          ></div>
          
          {/* Compact Popup */}
          <div className="fixed bottom-24 right-5 z-50 w-80 animate-fadeIn">
            <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
              {/* Header - Compact */}
              <div className="bg-gradient-to-r from-[#25D366] to-[#128C7E] p-3 text-white relative">
                <button
                  onClick={() => setShowPopup(false)}
                  className="absolute top-2 right-2 text-white hover:bg-white/20 rounded-full p-1 transition-all"
                  title={lang === 'es' ? 'Cerrar' : 'Close'}
                >
                  <X size={18} />
                </button>
                
                <div className="flex items-center gap-2">
                  <div className="bg-white p-1.5 rounded-full shadow-md">
                    <svg width="24" height="24" viewBox="0 0 32 32" fill="none">
                      <path d="M16 0C7.164 0 0 7.164 0 16c0 2.832.744 5.488 2.048 7.792L0 32l8.416-2.208A15.91 15.91 0 0016 32c8.836 0 16-7.164 16-16S24.836 0 16 0z" fill="#25D366"/>
                      <path d="M25.328 22.768c-.352.992-2.072 1.824-3.392 2.064-1.32.24-2.144.184-6.216-1.336-4.072-1.52-6.648-5.664-6.848-5.928-.2-.264-1.64-2.184-1.64-4.168 0-1.984 1.04-2.96 1.408-3.368.368-.408.8-.512 1.072-.512.272 0 .544.008.776.016.248.008.592-.096.928.704.336.8 1.152 2.816 1.256 3.016.104.2.176.44.04.704-.136.264-.2.432-.4.664-.2.232-.416.52-.592.696-.2.2-.408.416-.176.816.232.4 1.032 1.704 2.216 2.76 1.528 1.36 2.808 1.784 3.208 1.984.4.2.632.168.864-.096.232-.264.992-1.168 1.256-1.568.264-.4.528-.336.888-.2.36.136 2.296 1.08 2.688 1.28.392.2.656.296.752.456.096.16.096.928-.256 1.92z" fill="#fff"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-base font-bold">{t('whatsapp.title')}</h3>
                    <p className="text-white/90 text-xs">{t('whatsapp.subtitle')}</p>
                  </div>
                </div>
              </div>

              {/* Content - Compact */}
              <div className="p-4 space-y-3">
                {/* Business Description - Concise */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-gray-700 text-sm leading-relaxed">
                    {t('whatsapp.description')}
                  </p>
                </div>

                {/* QR Code Section - Smaller */}
                <div className="text-center space-y-2">
                  <p className="text-gray-800 text-sm font-semibold flex items-center justify-center gap-1">
                    <QrCode size={16} className="text-[#25D366]" />
                    {t('whatsapp.scanQR')}
                  </p>
                  
                  {/* QR Code - Compact */}
                  <div className="inline-block p-2 bg-gradient-to-br from-[#25D366]/10 to-[#128C7E]/10 rounded-lg border-2 border-[#25D366]">
                    <img 
                      src={qrCodeUrl} 
                      alt="WhatsApp QR Code" 
                      className="w-36 h-36 rounded bg-white"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div class="w-36 h-36 flex items-center justify-center bg-white rounded"><p class="text-xs text-gray-600">Loading...</p></div>';
                      }}
                    />
                  </div>
                  <p className="text-xs text-gray-500">{t('whatsapp.qrInstruction')}</p>
                </div>

                {/* Divider */}
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-px bg-gray-300"></div>
                  <span className="text-gray-500 text-xs">{t('whatsapp.or')}</span>
                  <div className="flex-1 h-px bg-gray-300"></div>
                </div>

                {/* Start Chat Button - Compact */}
                <button
                  onClick={handleStartChat}
                  className="w-full bg-gradient-to-r from-[#25D366] to-[#128C7E] text-white py-2.5 px-4 rounded-lg font-semibold hover:from-[#128C7E] hover:to-[#25D366] transition-all transform hover:scale-105 shadow-md flex items-center justify-center gap-2"
                >
                  <MessageCircle size={18} />
                  <span className="text-sm">{t('whatsapp.startChat')}</span>
                </button>
                
                <p className="text-xs text-gray-500 text-center">{t('whatsapp.opensApp')}</p>
              </div>

              {/* Footer - Compact */}
              <div className="bg-gray-50 px-4 py-2 border-t border-gray-100">
                <div className="flex items-center justify-center gap-1.5 text-xs text-gray-600">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.124.561 4.116 1.536 5.844L0 24l6.312-1.656A11.93 11.93 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" fill="#25D366"/>
                    <path d="M18.996 17.076c-.264.744-1.554 1.368-2.544 1.548-.99.18-1.608.138-4.662-1.002-3.054-1.14-4.986-4.248-5.136-4.446-.15-.198-1.23-1.638-1.23-3.126 0-1.488.78-2.22 1.056-2.526.276-.306.6-.384.804-.384.204 0 .408.006.582.012.186.006.444-.072.696.528.252.6.864 2.112.942 2.262.078.15.132.33.03.528-.102.198-.15.324-.3.498-.15.174-.312.39-.444.522-.15.15-.306.312-.132.612.174.3.774 1.278 1.662 2.07 1.146 1.02 2.106 1.338 2.406 1.488.3.15.474.126.648-.072.174-.198.744-.876.942-1.176.198-.3.396-.252.666-.15.27.102 1.722.81 2.016.96.294.15.492.222.564.342.072.12.072.696-.192 1.44z" fill="#fff"/>
                  </svg>
                  <span className="font-medium">{t('whatsapp.poweredBy')}</span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* CSS Animation */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default WhatsAppQRWidget;
